import { useState, useCallback, useEffect } from 'react';
import { retrieveItem } from '../utils/helpers';
import toggleToast from '../utils/messaging';
import {
  IResponseData,
  IGenerateRequest,
  IResponseChecklist,
  IResponseTranscript,
  IResponseForm,
} from '../global/interfaces';
import { axiosRequest } from '../utils/axiosRequest';

const useGenerativeAI = () => {
  const axios = axiosRequest();
  const [generateRequest, setGenerateRequest] = useState<
    IGenerateRequest | undefined
  >();

  const [responseChunk, setResponseChunk] = useState<IResponseData>({
    bookingId: 0,
    regenerateIndex: 0,
    response: '',
  });

  const generateChunkedResponse = useCallback(async () => {
    console.log('::::inside useCallback generateChunkedResponse...');
    try {
      setErrorGeneratingResponse(false);

      if (!generateRequest) {
        setIsGeneratingResponse(false);
        return;
      }

      if (generateRequest.type !== 'Prompt') return;
      setIsGeneratingResponse(true);

      const { bookingId, regenerateIndex } = generateRequest;

      setResponseChunk({
        bookingId,
        regenerateIndex,
        response: '',
      });

      const token = await retrieveItem('hp_auth_token');
      const res = await fetch(
        `${window._env_?.API_SERVER_URL || process.env.REACT_APP_API_SERVER_URL}/ai/generate_response_chunks`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            Accept: 'text/plain',
          },
          body: JSON.stringify(generateRequest),
        },
      );

      const reader = res?.body?.getReader();
      if (!reader) return;

      const decoder = new TextDecoder('utf-8');

      while (true) {
        const { value, done } = await reader.read();

        if (value) {
          const chunk = decoder
            .decode(value, { stream: true })
            .replace('<<END>>', '');

          console.log('::::http chunk:', chunk);

          if (res.status === 200) {
            if (chunk.includes('<<AI_USAGE>>')) {
              const preStr = chunk.substring(0, chunk.indexOf('<<AI_USAGE>>'));
              setResponseChunk((prev) => ({
                ...prev,
                response: prev?.response ? prev.response + preStr : preStr,
              }));

              const aiUsageStr = chunk.substring(
                chunk.indexOf('<<AI_USAGE>>') + 12,
              );

              try {
                const ai_usage = JSON.parse(aiUsageStr);
                console.log('::::::::ai_usage:', ai_usage);

                setResponseChunk((prev) => ({
                  ...prev,
                  message_id: ai_usage.message_id,
                  model: ai_usage.model,
                  usage: ai_usage.usage,
                }));

                saveAIUsage(
                  generateRequest.bookingId,
                  new Date().toISOString(),
                  ai_usage.message_id,
                  ai_usage.model,
                  ai_usage.usage,
                );
              } catch (error) {
                console.error('Error parsing AI usage:', error);
                throw 'Unable to parse AI usage stats, this is not a critical error.';
              }
            } else {
              setResponseChunk((prev) => ({
                bookingId,
                regenerateIndex,
                response: prev?.response ? prev.response + chunk : chunk,
              }));
            }
          } else {
            const error = JSON.parse(chunk);
            throw new Error(`${res.statusText}\n\n${error.error.message}`);
          }
        }

        if (done) break;
      }

      setTimeout(() => {
        setResponseChunk((prev) => ({ ...responseChunk, complete: true }));
        toggleToast('AI generate section is complete', 'success');
        setIsGeneratingResponse(false);
      }, 250);
    } catch (error: any) {
      console.error('Error:', error);
      setErrorGeneratingResponse(true);
      setIsGeneratingResponse(false);

      alert(error.message);

      toggleToast(error.message, 'error');
    }
  }, [generateRequest]);

  const [responseChecklist, setResponseChecklist] =
    useState<IResponseChecklist | null>(null);

  const [responseForm, setResponseForm] = useState<IResponseForm | null>(null);

  const generateResponse = useCallback(async () => {
    console.log(
      '::::inside useCallback generateResponse generateRequest: ',
      generateRequest,
    );
    try {
      setErrorGeneratingResponse(false);

      if (
        !generateRequest ||
        generateRequest.index === undefined ||
        generateRequest.index > (generateRequest.chunks?.length || -1)
      ) {
        if (!generateRequest?.chunks?.length) {
          alert('Transcript is empty');
          setGenerateRequest(undefined);
        }
        setIsGeneratingResponse(false);

        return;
      }

      if (
        !['Checklist', 'Form'].includes(generateRequest.type) ||
        !generateRequest.chunks?.length
      )
        return;

      setIsGeneratingResponse(true);

      const { chunks, index, regenerateIndex, type, ...payload } =
        generateRequest;

      const response = await axios.post(`ai/generate_response`, {
        ...payload,
        type,
        process_text: chunks[index].sentences,
        previous_text: index === 0 ? '' : chunks[index - 1].sentences,
      });

      console.log(':::::generateResponse response:', response.data);

      if (response.data?.status === 'success') {
        if (type === 'Checklist') {
          setResponseChecklist(response.data.data as IResponseChecklist);
        } else {
          setResponseForm(response.data.data as IResponseForm);
        }

        if (response.data.data.usage) {
          saveAIUsage(
            generateRequest.bookingId,
            new Date().toISOString(),
            response.data.data.message_id,
            response.data.data.model,
            response.data.data.usage,
          );
        }
      }
    } catch (error: any) {
      console.error('2:::::Error:', error);
      setErrorGeneratingResponse(true);
      setIsGeneratingResponse(false);
    }
    setIsGeneratingResponse(false);
  }, [generateRequest]);

  const saveAIUsage = async (
    bookingId: number,
    timestamp: string,
    message_id: string,
    model: string,
    usage: any,
  ) => {
    if (!message_id || !model || !usage || !bookingId) return;

    axios
      .post(`sessions/ai_usage/${bookingId}`, {
        timestamp,
        message_id,
        model,
        usage,
      })
      .then((resp) => {
        console.log('::::saveAIUsage resp: ', resp);
        if (resp.data.status === 'success') {
          console.log('usage saved successfully');
        } else {
          console.log('error saving usage');
        }
      })
      .catch((err) => {
        console.error('Error saving usage:', err);
      });
  };

  const [responseTranscript, setResponseTranscript] =
    useState<IResponseTranscript | null>(null);

  const generateTranscriptResponse = useCallback(async () => {
    console.log(
      '::::inside useEffect generateTranscriptResponse generateRequest?.index: ',
      generateRequest?.index,
    );
    try {
      setErrorGeneratingResponse(false);

      if (!generateRequest || generateRequest.index === undefined) {
        setIsGeneratingResponse(false);
        return;
      }

      if (generateRequest.type !== 'Transcript' || !generateRequest.chunks)
        return;

      setIsGeneratingResponse(true);

      const { chunks, index, regenerateIndex, type, ...payload } =
        generateRequest;

      const response = await axios.post(`ai/generate_response`, {
        ...payload,
        process_text: chunks[index].sentences,
        previous_text: index === 0 ? '' : chunks[index - 1].sentences,
      });

      console.log(':::::generateResponse response:', response.data);

      if (response.data?.status === 'success') {
        setResponseTranscript(response.data.data as IResponseTranscript);
      }
    } catch (error: any) {
      console.error('3:::::Error:', error);
      setErrorGeneratingResponse(true);
      setIsGeneratingResponse(false);
    }
    setIsGeneratingResponse(false);
  }, [generateRequest]);

  const [isGeneratingResponse, setIsGeneratingResponse] = useState(false);
  const [errorGeneratingResponse, setErrorGeneratingResponse] =
    useState<boolean>(false);

  useEffect(() => {
    if (!generateRequest) return;

    generateRequest?.messages && generateChunkedResponse();
    (generateRequest?.type === 'Checklist' ||
      generateRequest?.type === 'Form') &&
      generateResponse();
    generateRequest?.type === 'Transcript' && generateTranscriptResponse();
  }, [
    generateRequest,
    generateChunkedResponse,
    generateResponse,
    generateTranscriptResponse,
  ]);

  return {
    generateRequest,
    setGenerateRequest,
    responseChunk,
    responseChecklist,
    responseForm,
    responseTranscript,
    isGeneratingResponse,
    errorGeneratingResponse,
  };
};

export default useGenerativeAI;
